import { CADC, USDC } from "@/config/tokens";

type TokenKey = "USDC" | "CADC";

type Props = {
  readonly label: string;
  readonly token: TokenKey;
  readonly value: string;
  readonly placeholder: string;
  readonly onChange?: (v: string) => void;
  readonly onMax?: () => void;
  readonly hint?: string;
  readonly error?: string | null;
  readonly readOnly?: boolean;
};

const sanitise = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("")}`;
};

const tokenMeta = (t: TokenKey) =>
  t === "USDC"
    ? { symbol: USDC.symbol, src: "/usdc.svg" }
    : { symbol: CADC.symbol, src: "/cadc.svg" };

export const UnwindAmountInput = ({
  label,
  token,
  value,
  placeholder,
  onChange,
  onMax,
  hint,
  error,
  readOnly,
}: Props) => {
  const meta = tokenMeta(token);
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-3">
        <h3>{label}</h3>
        {readOnly ? (
          <span
            className="font-mono text-[10px] tracking-[0.16em] uppercase"
            style={{ color: "var(--ink-3)" }}
          >
            {hint ?? ""}
          </span>
        ) : (
          <button
            type="button"
            onClick={onMax}
            className="font-mono text-[10px] tracking-[0.16em] uppercase transition-opacity hover:opacity-70"
            style={{ color: "var(--ink-3)" }}
          >
            {hint ?? "Max"}
          </button>
        )}
      </div>

      <div
        className="flex items-center gap-4 pl-5 pr-6 py-4 md:py-3 rounded-[var(--radius-md)] surface-strong transition-all"
        style={{
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: error ? "var(--accent-rose)" : "var(--line-2)",
        }}
      >
        <input
          inputMode="decimal"
          placeholder={placeholder}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange?.(sanitise(e.target.value))}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none focus:outline-none focus-visible:outline-none font-serif text-[20px] md:text-[28px] tabular"
          style={{
            fontWeight: 300,
            letterSpacing: "-0.02em",
            color: "var(--ink-1)",
          }}
        />
        <div className="flex items-center gap-2 pl-4 border-l hairline shrink-0">
          {meta.src && (
            <img
              src={meta.src}
              alt=""
              aria-hidden
              className="w-6 h-6 rounded-full"
            />
          )}
          <span
            className="font-sans text-[13px] tracking-[0.04em]"
            style={{ color: "var(--ink-1)", fontWeight: 500 }}
          >
            {meta.symbol}
          </span>
        </div>
      </div>
      {error && (
        <p
          className="mt-2 font-mono text-[11px] tracking-[0.04em]"
          style={{ color: "var(--accent-rose)" }}
        >
          {error}
        </p>
      )}
    </label>
  );
};
